'use client';

import { FC, ReactNode, useState } from 'react';
import * as RadixCollapsible from '@radix-ui/react-collapsible';
import { css, cx } from '@bts-web/utils-style-engine';
import { RegularChevronDown } from '../../../common/svgIcons';

export interface CollapsibleHeaderProps {
  LeftContentIndicatorSlot: ReactNode;
  IconComponent?: ReactNode;
  RightContentIndicatorSlot?: string;
}

export type CollapsibleProps = {
  open?: boolean;
  header: CollapsibleHeaderProps;
  children: ReactNode;
  className?: string;
  contentClassName?: string;
};

const rootCn = css({
  backgroundColor: 'screen_background.primary',
  '& [data-element="right-content-slot-wrap"]': {
    display: 'flex',
    alignItems: 'center',
    gap: 'extra_small_3',
  },
  '& [data-element="trigger"]': {
    py: 'small',
    display: 'flex',
    width: '100%',
    alignItems: 'center',
    justifyContent: 'space-between',
    color: 'neutrals.text_primary',
    fontSize: 'title.medium',
    fontWeight: 'title.medium',
    letterSpacing: 'title.medium',
    lineHeight: 'title.medium',
    cursor: 'pointer',
  },
  '& [data-element="icon"]': {
    display: 'flex',
    transition: 'transform 300ms cubic-bezier(0.87, 0, 0.13, 1)',
  },
  '&[data-state="open"] [data-element="icon"]': {
    transform: 'rotate(90deg)',
  },
});

const contentBaseCn = css({
  overflow: 'hidden',
  backgroundColor: 'screen_background.primary',
  '&[data-state="open"]': {
    animation: `slideCollapseDown 300ms cubic-bezier(0.87, 0, 0.13, 1)`,
  },
  '&[data-state="closed"]': {
    animation: `slideCollapseUp 300ms cubic-bezier(0.87, 0, 0.13, 1)`,
  },
});

export const Collapsible: FC<CollapsibleProps> = ({
  children,
  header,
  open = false,
  className,
  contentClassName,
}) => {
  const [isCollapseOpen, setCollapseOpen] = useState(open);

  const {
    LeftContentIndicatorSlot,
    RightContentIndicatorSlot,
    ...restHeaderProps
  } = header;

  return (
    <RadixCollapsible.Root
      className={cx(rootCn, className)}
      open={isCollapseOpen}
      onOpenChange={setCollapseOpen}
      data-testid="collapsible"
    >
      <RadixCollapsible.Trigger
        {...restHeaderProps}
        data-element="trigger"
        data-testid="trigger"
        asChild
      >
        <button>
          <span data-element="left-content-slot-wrap">
            {LeftContentIndicatorSlot}
          </span>

          <span data-element="right-content-slot-wrap">
            {RightContentIndicatorSlot}
            <span aria-hidden data-element="icon" data-testid="icon">
              <RegularChevronDown size={18} />
            </span>
          </span>
        </button>
      </RadixCollapsible.Trigger>

      <RadixCollapsible.Content
        data-element="content"
        data-testid="content"
        className={cx(contentBaseCn, contentClassName)}
      >
        {children}
      </RadixCollapsible.Content>
    </RadixCollapsible.Root>
  );
};
